import React, { useState, useRef } from 'react';
import { Edit, Trash2, Download, Printer, FileImage, RefreshCw, FileCode2, Check } from 'lucide-react';
import { toPng } from 'html-to-image';
import QRCodeLib from 'qrcode';
import IdCardV2 from '../idcard/IdCardV2';

const ParticipantCard = ({ participant, event, showCard, onDelete, onArchive, onRestore, onRefresh, elementStyles, globalVisibility, index, selectionMode, selected, onSelect }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(participant.data || {});
    const [uncheckIn, setUncheckIn] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const cardRef = useRef(null);

    const handleUpdate = async () => {
        try {
            const bodyPayload = { data: editData };
            if (uncheckIn) {
                bodyPayload.isCheckedIn = false;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}/participants/${participant._id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(bodyPayload)
                }
            );
            const result = await response.json();
            if (result.success) {
                setIsEditing(false);
                setUncheckIn(false);
                onRefresh();
            }
        } catch (error) {
            console.error('Error updating participant:', error);
        }
    };

    const handleDownloadPNG = async (withBackground = true) => {
        if (!cardRef.current) return;

        const element = cardRef.current;
        const backgroundImage = element.querySelector('img');
        const originalDisplay = backgroundImage ? backgroundImage.style.display : null;

        if (!withBackground && backgroundImage) {
            backgroundImage.style.display = 'none';
        }

        setIsDownloading(true);

        try {
            const dataUrl = await toPng(element, {
                cacheBust: true,
                backgroundColor: null,
                quality: 1,
                pixelRatio: 3,
                skipFonts: true,
                preferredFontFormat: 'woff2'
            });

            const link = document.createElement('a');
            const firstField = event.participantFields?.[0];
        const fileName = (firstField && (editData[firstField.fieldName] ?? editData[firstField.id])) || 'participant';
            link.download = `${fileName}-card${withBackground ? '' : '-no-bg'}.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error('Could not download the image', error);
        } finally {
            if (!withBackground && backgroundImage) {
                backgroundImage.style.display = originalDisplay;
            }
            setIsDownloading(false);
        }
    };

    const handlePrint = async (withBackground = true) => {
        if (!cardRef.current) return;

        const element = cardRef.current;
        const backgroundImage = element.querySelector('img');
        const originalDisplay = backgroundImage ? backgroundImage.style.display : null;

        if (!withBackground && backgroundImage) {
            backgroundImage.style.display = 'none';
        }

        setIsPrinting(true);

        try {
            const dataUrl = await toPng(element, {
                cacheBust: true,
                backgroundColor: null,
                quality: 1,
                pixelRatio: 3,
                skipFonts: true,
                preferredFontFormat: 'woff2'
            });

            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                    <head>
                        <title>Print ID Card</title>
                        <style>
                            @page {
                                margin: 0;
                                size: auto;
                            }
                            body {
                                margin: 0;
                                padding: 0;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                height: 100vh;
                            }
                            img {
                                max-width: 100%;
                                max-height: 100vh;
                                object-fit: contain;
                            }
                        </style>
                    </head>
                    <body>
                        <img src="${dataUrl}" />
                        <script>
                            window.onload = function() {
                                setTimeout(function() {
                                    window.print();
                                }, 250);
                            };
                            window.onafterprint = function() {
                                window.close();
                            };
                        </script>
                    </body>
                </html>
            `);
            printWindow.document.close();
        } catch (error) {
            console.error('Could not print the image', error);
        } finally {
            if (!withBackground && backgroundImage) {
                backgroundImage.style.display = originalDisplay;
            }
            setIsPrinting(false);
        }
    };

    const printAsSVG = async () => {
        const fields = event?.participantFields?.filter(f => f.fieldType !== 'file') || [];
        let rows = '';
        fields.forEach(field => {
            const value = participant.data?.[field.fieldName] ?? participant.data?.[field.id];
            if (!value) return;
            if (!/name/.test(field.fieldName.toLowerCase())) return;
            const fontSize = value.length <= 20 ? 16 : value.length <= 25 ? 15 : 12;
            rows += `<p style="font-size:${fontSize}px;font-weight:bold;margin:2px 0;padding:0;max-width:100%;word-break:break-word;">${value}</p>`;
        });

        // Generate QR code as PNG data URL — works reliably in all environments
        const qrValue = `${window.location.origin}/v2/scan/${participant._id}`;
        let qrDataUrl = '';
        try {
            qrDataUrl = await QRCodeLib.toDataURL(qrValue, {
                width: 200,
                margin: 1,
                errorCorrectionLevel: 'H',
                color: { dark: '#000000', light: '#ffffff' }
            });
        } catch (e) {
            console.error('QR generation failed', e);
        }

        const contentHtml = `
            <div style="text-align:center;font-family:Arial,sans-serif;display:flex;flex-direction:column;align-items:center;">
                <div style="max-height:42px;overflow:hidden;width:100%;">
                    ${rows}
                </div>
                ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:72px;height:72px;flex-shrink:0;margin:4px auto 0;display:block;" />` : ''}
            </div>`;

        const orientation = globalVisibility?.isLandscapePrint ? 'landscape' : 'portrait';
        const rotationStyle = orientation === 'landscape'
            ? 'transform: rotate(-90deg); transform-origin: center center;'
            : '';

        const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml"
             style="box-sizing:border-box;width:100%;height:100%;display:flex;align-items:center;justify-content:center;">
          <div style="${rotationStyle}">
            ${contentHtml}
          </div>
        </div>
      </foreignObject>
    </svg>`;

        const page = `
    <html>
      <head>
        <style>
          @page { margin: 0; size: auto; }
          html, body { margin:0; padding:0; width:100%; height:100%; overflow:hidden; }
        </style>
      </head>
      <body style="text-align:center;font-family:Arial,sans-serif;">
        ${svg}
        <script>
          window.onload = () => { window.print(); };
          window.onafterprint = () => { window.close(); };
        </script>
      </body>
    </html>`;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return alert('Please enable pop-ups to print.');
        printWindow.document.open();
        printWindow.document.write(page);
        printWindow.document.close();
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this participant?')) {
            await onDelete(participant._id);
        }
    };

    return (
        <div
            className={`relative mb-8 rounded-lg transition-all ${selectionMode
                ? selected
                    ? 'border-2 border-blue-500 shadow-md shadow-blue-100'
                    : 'border-2 border-gray-300 hover:border-blue-300 cursor-pointer'
                : 'border border-gray-300'
                }`}
            style={{ width: '301px' }}
            onClick={selectionMode ? onSelect : undefined}
        >
            {/* Selection checkbox */}
            {selectionMode && (
                <div className="absolute top-2 left-2 z-30">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${selected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-400'
                        }`}>
                        {selected && <Check className="w-4 h-4 text-white" />}
                    </div>
                </div>
            )}

            {/* ID Card - Scaled to 70% */}
            {showCard && (
                <div
                    ref={cardRef}
                    style={{
                        transform: 'scale(0.7)',
                        transformOrigin: 'top left',
                        width: '430px',
                        height: '610px',
                        marginBottom: '-183px' // Compensate for scale: 610 * (1 - 0.7) = 183
                    }}
                >
                    <IdCardV2
                        participant={participant}
                        event={event}
                        elementStyles={elementStyles}
                        globalVisibility={globalVisibility}
                        index={index}
                    />
                </div>
            )}

            {/* Control Panel */}
            <div className="bg-white border-t border-gray-200 rounded-b-lg p-4">
                {isEditing ? (
                    <div className="space-y-3">
                        <h3 className="font-semibold text-gray-800 mb-2">Edit Participant</h3>
                        {event.participantFields?.map(field => {
                            const val = editData[field.fieldName] ?? editData[field.id] ?? '';
                            const setVal = (v) => setEditData({ ...editData, [field.fieldName]: v });
                            return (
                                <div key={field.id}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {field.fieldName}
                                    </label>
                                    {field.fieldType === 'textarea' ? (
                                        <textarea
                                            value={val}
                                            onChange={(e) => setVal(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                            rows="3"
                                        />
                                    ) : field.fieldType === 'file' ? (
                                        <input
                                            type="url"
                                            value={val}
                                            onChange={(e) => setVal(e.target.value)}
                                            placeholder="Enter image URL"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        />
                                    ) : (
                                        <input
                                            type={field.fieldType === 'email' ? 'email' : field.fieldType === 'number' ? 'number' : 'text'}
                                            value={val}
                                            onChange={(e) => setVal(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        />
                                    )}
                                </div>
                            );
                        })}
                        {participant.isCheckedIn && (
                            <div className="flex items-center gap-2 mt-3 p-3 bg-blue-50/50 rounded-md border border-blue-100 text-sm">
                                <input
                                    type="checkbox"
                                    id={`uncheckIn-${participant._id}`}
                                    checked={uncheckIn}
                                    onChange={(e) => setUncheckIn(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                />
                                <label htmlFor={`uncheckIn-${participant._id}`} className="text-gray-700 font-medium cursor-pointer">
                                    Undo Venue Check-in
                                </label>
                            </div>
                        )}
                        <div className="flex gap-2 pt-2">
                            <button
                                onClick={handleUpdate}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                            >
                                Save Changes
                            </button>
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditData(participant.data || {});
                                    setUncheckIn(false);
                                }}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        {(() => {
                            const category = participant.categoryId && event.participantCategories?.find(c => c.id === participant.categoryId);
                            return category ? (
                                <div className="mb-3 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-center">
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</span>
                                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{category.name}</p>
                                </div>
                            ) : null;
                        })()}

                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                            >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                            </button>
                            <button
                                onClick={() => handleDownloadPNG(true)}
                                disabled={isDownloading}
                                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50"
                            >
                                <Download className="w-4 h-4 mr-1" />
                                {isDownloading ? 'Downloading...' : 'PNG'}
                            </button>
                            <button
                                onClick={() => handleDownloadPNG(false)}
                                disabled={isDownloading}
                                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100 disabled:opacity-50"
                            >
                                <FileImage className="w-4 h-4 mr-1" />
                                No BG
                            </button>
                            <button
                                onClick={() => handlePrint(true)}
                                disabled={isPrinting}
                                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100 disabled:opacity-50"
                            >
                                <Printer className="w-4 h-4 mr-1" />
                                {isPrinting ? 'Printing...' : 'Print'}
                            </button>
                            <button
                                onClick={printAsSVG}
                                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-teal-700 bg-teal-50 rounded hover:bg-teal-100 col-span-2"
                            >
                                <FileCode2 className="w-4 h-4 mr-1" />
                                Print SVG
                            </button>

                            {participant.isArchived ? (
                                <>
                                    <button
                                        onClick={() => onRestore && onRestore(participant._id)}
                                        className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 col-span-1"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-1" />
                                        Restore
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 col-span-1"
                                    >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Delete Forever
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => onArchive && onArchive(participant._id)}
                                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 rounded hover:bg-orange-100 col-span-2"
                                >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Archive
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParticipantCard;